import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import service from '../service';
import type {
  PartnerReferralCode,
  UpdatePartnerReferralCodePayload,
} from '../types/partnerReferralCode';
import { partnerReferralCodeQueryKeys } from './usePartnerReferralCodes';

export interface UpdatePartnerReferralCodeVariables {
  id: string;
  payload: UpdatePartnerReferralCodePayload;
}

export function useUpdatePartnerReferralCode(
  options?: UseMutationOptions<
    PartnerReferralCode,
    Error,
    UpdatePartnerReferralCodeVariables
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['partnerReferralCode', 'update'],
    mutationFn: async ({ id, payload }: UpdatePartnerReferralCodeVariables) => {
      return service.updatePartnerReferralCode(id, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({
        queryKey: partnerReferralCodeQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: partnerReferralCodeQueryKeys.detail(variables.id),
      });
      onSuccess?.(data, variables, context, mutation);
    },
  });
}
